import React, { useEffect, useState } from 'react';
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Box, Button, Card, Typography } from '@mui/material';
import { useFetchDataQuery, useInputOutputLookupQuery, useProcessMakingCustomerQuery, useProcessStatusLookupQuery, useProcessTypeLookupQuery } from '../../../components/services/services';
import { useGetDynamicReceiptMutation } from '../services';
import { filterOperatorsDateOnly } from '../../../utils/filter-date-time';
import { filterOperatorsAmountMax999M } from '../../../utils/filter-amount';
import { formatMoney, formatMoneyValue } from '../../../utils/currencyConvert';
import { getCurrencyName, getCurrencyValueOptions } from '../../../utils/currencyLookup';
import Table from '../../../components/table/Table';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { getProccesTypesList } from '../../../utils/global';
import { enqueueSnackbar } from 'notistack';
import { Security as SecurityIcon } from '@mui/icons-material';

interface TransactionTableProps {
    setDocumentData: React.Dispatch<React.SetStateAction<any>>;
    canViewTransactionList: boolean;
    isPermissionLoading: boolean;
}

const TransactionTable: React.FC<TransactionTableProps> = ({
    setDocumentData,
    canViewTransactionList,
    isPermissionLoading
}) => {
    const [getDynamicReceipt, { isLoading: isDownloadLoading }] = useGetDynamicReceiptMutation();
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState);

    const { data: processEnumData } = useProcessTypeLookupQuery(undefined, {
        skip: !currentUserState?.companyId || !canViewTransactionList || isPermissionLoading,
    });
    const { data: processStatusLookup } = useProcessStatusLookupQuery(undefined, {
        skip: !currentUserState?.companyId || !canViewTransactionList || isPermissionLoading,
    });
    const { data: processMakingCustomerLookup, } = useProcessMakingCustomerQuery(
        { companyId: currentUserState?.companyId },
        { skip: !currentUserState?.companyId || !canViewTransactionList || isPermissionLoading }
    );
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({
    });
    const { data: inputOutputLookupData } = useInputOutputLookupQuery(undefined, {
        skip: !currentUserState?.companyId || !canViewTransactionList || isPermissionLoading,
    });

    const handleDownloadReceipt = async (referenceNumber: string) => {
        try {
            const payload = { referenceNumber };
            const result = await getDynamicReceipt(payload);
            if (result.data) {
                setDocumentData(result.data);
            }
        } catch (error) {
            enqueueSnackbar('Dekont indirilirken hata oluştu.', { variant: 'error' });
        }
    };

    const initialColumns: GridColDef[] = [
        {
            field: 'showDecont',
            headerName: 'İşlemler',
            width: 180,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                // Sadece status === 1 ise buton göster
                if (params.row.status !== 1) {
                    return null;
                }

                return (
                    <Button
                        onClick={() => handleDownloadReceipt(params.row.referenceNumber)}
                        disabled={isDownloadLoading || !params.row.referenceNumber}
                        sx={{ fontWeight: "500", height: "25px", minWidth: 'auto', fontSize: '.95rem', background: "#00aaff1a !important", color: `${isDownloadLoading || !params.row.referenceNumber ? "gray" : "#27abfb"} !important` }}
                    >
                        Dekont Görüntüle
                    </Button>
                );
            },
        },
        // @ts-ignore
        { field: 'cDate', headerName: 'İşlem Tarihi', width: 250, type: "datetime", filterOperators: filterOperatorsDateOnly },
        { field: 'type', headerName: 'İşlem Türü', width: 350, type: 'singleSelect', valueOptions: [], },
        {
            field: 'inOut',
            headerName: 'Para Giriş/Para Çıkış',
            width: 200,
            type: 'singleSelect',
            valueOptions: [],
        },
        { field: 'customerName', headerName: 'Gönderen Adı', width: 300 },
        { field: 'senderWalletNo', headerName: 'Gönderici Hesap No', width: 160 },
        { field: 'embossName', headerName: 'Kart Sahibi', width: 200 },
        { field: 'cardNo', headerName: 'Kart Numarası', width: 150 },
        /* {
            field: 'receiverValue', headerName: 'Alıcı Tutar', width: 80, type: "number",
            renderCell: (params: GridRenderCellParams) => formatMoney(params, 'receiverValue')
        }, */
        {
            field: 'fee', headerName: 'İşlem Ücreti', width: 120,
            filterOperators: filterOperatorsAmountMax999M,
            renderCell: (params: GridRenderCellParams) => formatMoney(params, 'fee'),
            valueFormatter: (value: any, row: any) => formatMoneyValue(value, row?.currencyId),
        },
        {
            field: 'currencyId', headerName: 'Para Birimi', width: 150,
            type: 'singleSelect',
            valueOptions: getCurrencyValueOptions(),
            renderCell: (params: GridRenderCellParams) => {
                const name = getCurrencyName(params.value);
                return `${name}`;
            }
        },
        { field: 'value', headerName: 'Tutar', width: 120, filterOperators: filterOperatorsAmountMax999M, renderCell: (params: GridRenderCellParams) => formatMoney(params, 'value'), valueFormatter: (value: any, row: any) => formatMoneyValue(value, row?.currencyId) },
        {
            field: 'transactionAfterMainBalance',
            headerName: 'Nakit Bakiye',
            width: 120,
            renderCell: (params: GridRenderCellParams) =>  formatMoney(params, 'transactionAfterMainBalance'),
            valueFormatter: (value: any, row: any) => formatMoneyValue(value, row?.currencyId),
            filterOperators: filterOperatorsAmountMax999M,
        },
        {
            field: 'transactionAfterMealBalance',
            headerName: 'Yemek Bakiyesi',
            width: 140,
            renderCell: (params: GridRenderCellParams) =>  formatMoney(params, 'transactionAfterMealBalance'),
            valueFormatter: (value: any, row: any) => formatMoneyValue(value, row?.currencyId),
            filterOperators: filterOperatorsAmountMax999M,
        },
        {
            field: 'transactionAfterCreditBalance',
            headerName: 'Harcama Bakiyesi',
            width: 140,
            renderCell: (params: GridRenderCellParams) =>  formatMoney(params, 'transactionAfterCreditBalance'),
            valueFormatter: (value: any, row: any) => formatMoneyValue(value, row?.currencyId),
            filterOperators: filterOperatorsAmountMax999M,
        },
        {
            field: 'transactionAfterPayPuanBalance',
            headerName: 'PayPuan Bakiye',
            width: 140,
            renderCell: (params: GridRenderCellParams) =>  formatMoney(params, 'transactionAfterPayPuanBalance'),
            valueFormatter: (value: any, row: any) => formatMoneyValue(value, row?.currencyId),
            filterOperators: filterOperatorsAmountMax999M,
        },
        { field: 'receiverName', headerName: 'Alıcı Adı', width: 300 },
        { field: 'receiverWalletNo', headerName: 'Alıcı Hesap No', width: 150 },
        { field: 'ibanNo', headerName: 'IBAN', width: 250 },
        {
            field: 'status', headerName: 'Durum', width: 200, type: 'singleSelect', valueOptions: processStatusLookup?.data.map((item: any) => ({
                value: item.value,
                label: item.text,
            }))
        },
        { field: 'referenceNumber', headerName: 'Referans No', width: 150 },
        { field: 'description', headerName: 'Açıklama', width: 200 },
        { field: 'transactionMakingCustomer', headerName: 'İşlemi Yapan Kullanıcı', width: 270, type: 'singleSelect', valueOptions: [], },
        //@ts-ignore
        { field: 'uDate', headerName: 'Güncelleme Tarihi', width: 250, type: "datetime", filterOperators: filterOperatorsDateOnly },
    ];
    const [requestBody, setRequestBody] = useState({
        filters: [],
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
        customerId: currentUserState?.companyId || undefined,
    });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
    const listUrl = "/Process/ListReportV2"

    const { data: fetchData, isFetching } = useFetchDataQuery({ url: listUrl, filter: requestBody, method: "POST" }, {
        skip: !canViewTransactionList || !requestBody?.customerId || isPermissionLoading,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: false,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        const updatedColumns = initialColumns.map(column => {
            if (column.field === 'type' && (processEnumData?.data ?? []).length > 0) {
                return {
                    ...column,
                    valueOptions: processEnumData?.data.map((item: any) => ({
                        value: item.value,
                        label: item.text,
                    })),
                };
            }
            if (column.field === 'status' && (processStatusLookup?.data ?? []).length > 0) {
                return {
                    ...column,
                    valueOptions: processStatusLookup?.data.map((item: any) => ({
                        value: item.value,
                        label: item.text,
                    })),
                };
            }
            if (column.field === 'inOut' && (inputOutputLookupData?.data ?? []).length > 0) {
                return {
                    ...column,
                    valueOptions: inputOutputLookupData?.data.map((item: any) => ({
                        value: item.value,
                        label: item.text,
                    })),
                };
            }
            if (column.field === 'transactionMakingCustomer' && (processMakingCustomerLookup?.data ?? []).length > 0) {

                return {
                    ...column,
                    valueOptions: processMakingCustomerLookup?.data.map((item: any) => ({
                        value: item.value,
                        label: item.text,
                    })),
                };
            }
            return column;
        });
        setColumns([...updatedColumns]);
    }, [processEnumData, fetchData?.data, currentUserState, processStatusLookup, processMakingCustomerLookup, inputOutputLookupData]);

    useEffect(() => {
        if (currentUserState?.companyId) {
            setRequestBody((prev) => ({ ...prev, customerId: currentUserState.companyId }));
        }
    }, [currentUserState])

    const filterTimeoutRef = React.useRef<any>(null);

    const onFilterChange = React.useCallback((filterObj: any) => {
        clearTimeout(filterTimeoutRef.current);
        filterTimeoutRef.current = setTimeout(() => {
            setRequestBody((prev) => ({ ...prev, filters: filterObj, page: 1 }));
        }, 500);
    }, []);

    const handleChangePagination = (model: any) => {
        setPaginationModel(model);
        setRequestBody((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page + 1 }));
    };


    const sortModelChange = (model: any) => {
        setRequestBody((prev) => ({ ...prev, orderBy: model[0]?.field ?? "", orderDesc: model[0]?.sort === "asc" }));
    }

    const getRowClassName = (params: any) => {
        const processType = getProccesTypesList.find(x => x.id === params.row?.type);
        const isCustomerTransaction = params.row?.customerId === currentUserState?.companyId;
        const indicator = isCustomerTransaction ? processType?.customerIndicator : processType?.receiverIndicator;
        ;
        if (indicator === 1) {
            return 'money-income-row'; // Para girişi - soft yeşil
        } else if (indicator === -1) {
            return 'money-outcome-row'; // Para çıkışı - soft kırmızı
        }
        return 'no-money-line';
    };

    if (!isPermissionLoading && !canViewTransactionList) {
        return (
            <Box sx={{
                minHeight: 320,
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Card sx={{
                    maxWidth: 500,
                    p: 4,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
                    background: 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(252, 165, 165, 0.3)',
                    textAlign: 'center'
                }}>
                    <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
                        Erişim Engellendi
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
                        İşlem listesine erişim yetkiniz bulunmamaktadır.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
            }}
        >
            <Table
                initialState={{
                    pinnedColumns: {
                        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'actions'],
                        right: ['delete', 'edit'],
                    },
                } as GridInitialStatePro}
                sx={{
                    // height: window.innerHeight + "px !important",
                    margin: fetchData?.data?.length > 0 && paginationModel.pageSize > 25 ? "0px 0 25px 0 !important" : "0px !important",

                }}
                columnVisibilityConfigKey="TransactionTableFilterColumnVisibilityKey"
                filterKey="TransactionTableFilter"
                getRowClassName={getRowClassName}
                sortModelChange={sortModelChange}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                checkboxSelection
                isMasterDetailActive={false}
                headerFilters={true}
                rows={fetchData?.data ?? []}
                columns={columns}
                isLoading={isPermissionLoading || (canViewTransactionList && (isFetching || fetchData === undefined))}
                handleFilterChange={onFilterChange}
                paginationModel={paginationModel}
                onPaginationModelChange={handleChangePagination}
                rowCount={fetchData?.totalCount ?? 0}
                getRowId={(row: any) => row.id}
                idName={"id"}
                listUrl={listUrl}
                fetchData={fetchData}
                showDownloadButton={true}
                listName="İşlem Listesi"
                requestBody={requestBody}
                fetchMethod="POST"
            />
        </Box>
    );
};

export default TransactionTable;

